import { IconDeviceDesktop } from "@tabler/icons-react";
import { useState } from "react";
import { BillingSectionHeader } from "@/components/billing/billing-section-header";
import { UsageTable } from "@/components/billing/usage-table";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	type BillingUsageItem,
	formatFeatureName,
	getFeatureIcon,
	getUsageStatus,
	usageLimit,
	utilizationBadgeVariant,
} from "@/lib/billing-usage";
import { cn } from "@/lib/utils";

type UsageGridViewProps = {
	data: BillingUsageItem[];
	className?: string;
};

function UsageGridView({ data, className }: UsageGridViewProps) {
	return (
		<div
			className={cn(
				"grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3",
				className,
			)}
		>
			{data.map((item) => {
				const limit = usageLimit(item.limit);
				const Icon = getFeatureIcon(item.feature_key);
				const { status } = getUsageStatus(item.usage, limit);
				const percentage = limit ? (item.usage / limit) * 100 : 0;

				return (
					<Card
						key={`${item.feature_key}-${item.period_start}`}
						className="relative overflow-hidden"
					>
						<CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
							<CardTitle className="min-w-0 max-w-[24ch] truncate text-base/7 font-medium sm:text-sm/6">
								{formatFeatureName(item.feature_key)}
							</CardTitle>
							<Icon className="size-5 shrink-0 text-muted-foreground sm:size-4" />
						</CardHeader>
						<CardContent>
							<div className="space-y-3">
								<div className="flex items-center justify-between gap-2">
									<span className="text-3xl/9 font-bold sm:text-2xl/8">
										{item.usage.toLocaleString()}
									</span>
									{limit && (
										<Badge variant={utilizationBadgeVariant(status)}>
											{percentage.toFixed(0)}%
										</Badge>
									)}
								</div>
								{limit && (
									<>
										<Progress value={percentage} className="h-2" />
										<p className="text-base/7 text-muted-foreground sm:text-sm/6">
											{item.usage.toLocaleString()} of {limit.toLocaleString()}{" "}
											used
										</p>
									</>
								)}
								<div className="border-t pt-2 text-base/7 text-muted-foreground sm:text-sm/6">
									<div className="flex flex-col gap-1 sm:flex-row sm:justify-between sm:gap-3">
										<span className="shrink-0">Period:</span>
										<span className="min-w-0 sm:text-right">
											{new Date(item.period_start).toLocaleDateString()} -{" "}
											{new Date(item.period_end).toLocaleDateString()}
										</span>
									</div>
								</div>
							</div>
						</CardContent>
					</Card>
				);
			})}
		</div>
	);
}

type InDepthUsageSectionProps = {
	data: BillingUsageItem[];
	loading: boolean;
	className?: string;
};

export function InDepthUsageSection({
	data,
	loading,
	className,
}: InDepthUsageSectionProps) {
	const [viewMode, setViewMode] = useState<"table" | "grid">("table");

	const viewToggle = (
		<div className="w-full overflow-x-auto sm:w-auto">
			<Tabs
				value={viewMode}
				onValueChange={(v) => setViewMode(v as "table" | "grid")}
				className="w-max min-w-full sm:min-w-0"
			>
				<TabsList className="bg-muted/50">
					<TabsTrigger value="table" className="text-base/7 sm:text-sm/6">
						Table View
					</TabsTrigger>
					<TabsTrigger value="grid" className="text-base/7 sm:text-sm/6">
						Grid View
					</TabsTrigger>
				</TabsList>
			</Tabs>
		</div>
	);

	return (
		<Card
			className={cn(
				"border border-border bg-card shadow-sm transition-shadow duration-300 hover:shadow-md",
				className,
			)}
		>
			<BillingSectionHeader
				title="Detailed Usage Breakdown"
				description="Complete overview of all features and metrics"
				action={viewToggle}
			/>

			<CardContent className="pt-0">
				{data.length > 0 ? (
					viewMode === "table" ? (
						<UsageTable data={data} loading={loading} />
					) : (
						<UsageGridView data={data} />
					)
				) : (
					<div className="py-16 text-center">
						<div className="mb-4 inline-flex size-20 items-center justify-center rounded-full bg-muted/50">
							<IconDeviceDesktop className="size-10 text-muted-foreground opacity-50" />
						</div>
						<p className="mb-2 text-lg font-semibold text-foreground">
							No usage data available
						</p>
						<p className="mx-auto max-w-[40ch] text-pretty text-base/7 text-muted-foreground sm:text-sm/6">
							Data will appear here once you start using features. Check back
							later to see your usage statistics.
						</p>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
